import { Check, Plus, X } from "lucide-react"
import { useEffect, useState } from "react"
import { cx } from "../lib/cx"
import type { Subtask } from "../types"

type Props = {
  taskId: string
}

const SubtaskChecklist = ({ taskId }: Props) => {
  const [subtasks, setSubtasks] = useState<Subtask[]>([])
  const [newTitle, setNewTitle] = useState("")
  const [showInput, setShowInput] = useState(false)

  useEffect(() => {
    fetch(`/api/tasks/${taskId}/subtasks`)
      .then((r) => r.json())
      .then((d) => setSubtasks(d.subtasks || []))
      .catch(() => {})
  }, [taskId])

  const completed = subtasks.filter((s) => s.completed).length
  const total = subtasks.length
  const pct = total > 0 ? (completed / total) * 100 : 0

  const handleToggle = async (subtask: Subtask) => {
    const newCompleted = !subtask.completed
    setSubtasks((prev) =>
      prev.map((s) => (s.id === subtask.id ? { ...s, completed: newCompleted } : s)),
    )
    try {
      await fetch(`/api/tasks/${taskId}/subtasks/${subtask.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ completed: newCompleted }),
      })
    } catch { /* ok */ }
  }

  const handleAdd = async () => {
    if (!newTitle.trim()) return
    try {
      const res = await fetch(`/api/tasks/${taskId}/subtasks`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: newTitle.trim() }),
      })
      if (res.ok) {
        const data = await res.json()
        setSubtasks((prev) => [...prev, data.subtask])
        setNewTitle("")
      }
    } catch { /* ok */ }
  }

  const handleDelete = async (subtaskId: string) => {
    setSubtasks((prev) => prev.filter((s) => s.id !== subtaskId))
    try {
      await fetch(`/api/tasks/${taskId}/subtasks/${subtaskId}`, { method: "DELETE" })
    } catch { /* ok */ }
  }

  return (
    <div>
      <label className="mb-2 flex items-center gap-2 text-xs font-medium text-slate-700">
        <Check className="h-4 w-4" />
        Subtasks {total > 0 && `(${completed}/${total})`}
      </label>

      {/* Progress bar */}
      {total > 0 && (
        <div className="mb-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
          <div
            className="h-full bg-moss transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>
      )}

      {/* Subtask items */}
      <div className="space-y-1">
        {subtasks.map((s) => (
          <div
            key={s.id}
            className="group flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-slate-50"
          >
            <button
              type="button"
              onClick={() => handleToggle(s)}
              className={cx(
                "flex h-4.5 w-4.5 items-center justify-center rounded border-2 transition",
                s.completed
                  ? "border-moss bg-moss text-white"
                  : "border-line hover:border-moss",
              )}
            >
              {s.completed && <Check className="h-3 w-3" />}
            </button>
            <span
              className={cx(
                "flex-1 text-sm",
                s.completed ? "text-stone line-through" : "text-slate-900",
              )}
            >
              {s.title}
            </span>
            <button
              type="button"
              onClick={() => handleDelete(s.id)}
              className="opacity-0 group-hover:opacity-100 flex h-5 w-5 items-center justify-center rounded text-stone hover:text-rose-600"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>

      {/* Add subtask */}
      {showInput ? (
        <div className="mt-2 flex gap-2">
          <input
            type="text"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleAdd()
              if (e.key === "Escape") { setNewTitle(""); setShowInput(false) }
            }}
            placeholder="Subtask title..."
            className="flex-1 rounded-lg border border-line bg-white px-3 py-1.5 text-sm outline-none focus:border-moss"
            autoFocus
          />
          <button
            type="button"
            onClick={handleAdd}
            className="rounded-lg bg-moss px-3 py-1.5 text-sm font-medium text-white hover:bg-moss/90"
          >
            Add
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setShowInput(true)}
          className="mt-2 flex items-center gap-1.5 text-xs font-medium text-stone hover:text-slate-700"
        >
          <Plus className="h-3.5 w-3.5" />
          Add item
        </button>
      )}
    </div>
  )
}

export default SubtaskChecklist
